<template>
  <div></div>
</template>
<script>
import _ from "lodash";
import _get from "lodash/get";
import _startCase from "lodash/startCase";
import _upperFirst from "lodash/upperFirst";
import * as d3 from "d3";
import * as utils from "../utils";
import { getContrast } from "../../../services/visualization/data-management";

const MAX_BUBBLE_RADIUS = 850000;
const MIN_BUBBLE_RADIUS = 450000;

let STATUS = {
  ACTIVE: "Active",
  ACTIVEWMODIFICATION: "Active with Modifications",
  DISQUALIFIED: "Disqualified",
  WITHDRAWSTUDY: "Withdrawn"
};

const e = Math; // shortcut for the mathematical function
const D2R = e.PI / 180.0; // value used for converting degrees to radians

// eslint-disable-next-line no-extend-native
Number.prototype.toRadians = function() {
  return this * D2R;
};

const distance = (lat0, lng0, lat1, lng1) => {
  const rlat0 = lat0.toRadians();
  const rlng0 = lng0.toRadians();
  const rlat1 = lat1.toRadians();
  const rlng1 = lng1.toRadians();

  const Δlat = rlat1 - rlat0;
  const Δlng = rlng1 - rlng0;
  const preA = e.pow(e.sin(Δlng / 2), 2) * e.cos(rlat0) * e.cos(rlat1);
  const a = e.pow(e.sin(Δlat / 2), 2) + preA;
  const c = 2 * e.asin(e.sqrt(a));
  const d = c * 6378137;

  return d;
};

const hasIntersections = (circle0, circle1) => {
  const center0 = circle0.center;
  const center1 = circle1.center;

  const maxDist = circle0.radius + circle1.radius;
  const actualDist = distance(center0.lat(), center0.lng(), center1.lat(), center1.lng());

  return maxDist >= actualDist;
};

export default {
  props: {
    google: Object,
    map: Object,
    circleData: Object,
    statusSelected: Array,
    mapZoom: Number,
    sitesSelected: [Array, String],
    countrySelected: [Array, String]
  },

  data() {
    return {
      newCircleData: this.circleData,
      newStatusData: this.statusSelected,
      circleMarkers: {
        ACTIVE: [],
        ACTIVEWMODIFICATION: [],
        DISQUALIFIED: [],
        WITHDRAWSTUDY: []
      },
      radiusScaler: null,
      min: 0,
      max: 100,
      zoom: null,
      legacyVal: true,
      studyId: this.$route.params.studyId
    };
  },

  watch: {
    max() {
      this.radiusScaler = d3
        .scaleLinear()
        .domain([this.min, this.max])
        .range([MIN_BUBBLE_RADIUS, MAX_BUBBLE_RADIUS]);
    },
    min() {
      this.radiusScaler = d3
        .scaleLinear()
        .domain([this.min, this.max])
        .range([MIN_BUBBLE_RADIUS, MAX_BUBBLE_RADIUS]);
    },
    circleData: async function(value) {
      this.newCircleData = value;
      this.prepareMapsData();
    },
    mapZoom: function(value) {
      this.zoom = value;
      this.removeAllCircle();
      this.renderCircle(this.newCircleData, this.newStatusData);
    },
    sitesSelected: async function(values) {
      if (this.sitesSelected[0] === "all") {
        this.flyOnTarget(this.newCircleData);
      } else {
        const chosenSites = {};
        const sites = Object.keys(this.newCircleData);
        for (const idx in sites) {
          const site = sites[idx];
          if (values.includes(site)) {
            chosenSites[site] = this.newCircleData[site];
          }
        }
        this.flyOnTarget(chosenSites);
      }
    },
    statusSelected: async function(values) {
      if (!utils.circleIsEmpty(this.circleMarkers)) {
        for (const status in this.circleMarkers) {
          this.circleMarkers[status].forEach(marker => {
            if (values.indexOf(marker.idStatus) === -1) {
              // this.google.maps.event.clearInstanceListeners(marker); // remove gray tooltip when status unselected. But probably not neccessary now
              marker.setMap(null);
            } else {
              // this.decorateCircles(marker); probably not necesary now. related to above
              marker.setMap(this.map);
            }
          });
        }
      }
    }
  },

  methods: {
    getCircleRadius(value) {
      const radiusScaler = d3
        .scaleLinear()
        .domain([this.min, this.max])
        .range([MIN_BUBBLE_RADIUS, MAX_BUBBLE_RADIUS]);
      const bubbleRadius = (radiusScaler(value) * Math.PI) / Math.pow(2, this.zoom);
      return bubbleRadius;
    },
    async renderIntersectCircle() {
      let apiPromises = [this.renderIntersectCircleByStatus("ACTIVE"), this.renderIntersectCircleByStatus("ACTIVEWMODIFICATION")];
      if (this.legacyVal) {
        apiPromises = apiPromises.concat([this.renderIntersectCircleByStatus("DISQUALIFIED"), this.renderIntersectCircleByStatus("WITHDRAWSTUDY")]);
      } else {
        apiPromises = apiPromises.concat([this.renderIntersectCircleByStatus("DISCONTINUED")]);
      }
      await Promise.all(apiPromises);
    },
    async renderIntersectCircleByStatus(status) {
      try {
        let startIdx = 0;
        const hasIntersect = true;
        // eslint-disable-next-line no-unmodified-loop-condition
        while (hasIntersect) {
          const markersLength = this.circleMarkers[status].length;
          if (startIdx === markersLength) {
            break;
          }
          const currCircle = this.circleMarkers[status][startIdx];
          const intersectedMarkers = _.remove(this.circleMarkers[status], c => {
            const sameCircle = currCircle.idMarker !== c.idMarker;
            const intersected = hasIntersections(currCircle, c);
            return sameCircle && intersected;
          });
          let totalLat = 0;
          let totalLng = 0;
          let totalQty = 0;
          const siteInfos = [];
          if (intersectedMarkers.length > 0) {
            intersectedMarkers.push(currCircle);
            _.remove(this.circleMarkers[status], c => {
              return currCircle.idMarker === c.idMarker;
            }); //add currCircle to be merged, removed from original array first
            intersectedMarkers.forEach(m => {
              m.setMap(null);
              totalLat += m.center.lat();
              totalLng += m.center.lng();
              totalQty += m.qtyStatus;
              siteInfos.push(...m.markerInfos);
            });
            const midPointLat = totalLat / intersectedMarkers.length;
            const midPointLng = totalLng / intersectedMarkers.length;
            const combineLatLng = new this.google.maps.LatLng(midPointLat, midPointLng);
            const circleMarker = new this.google.maps.Circle({
              idMarker: _.uniqueId(),
              strokeColor: utils.generateCircleStrokeColors(status),
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: utils.generateCircleColors(status),
              fillOpacity: 0.5,
              idStatus: status,
              map: this.map,
              qtyStatus: totalQty,
              radius: this.getCircleRadius(totalQty),
              center: combineLatLng,
              markerInfos: siteInfos
            });
            this.decorateCircles(circleMarker);
            this.circleMarkers[status].push(circleMarker);
          }
          startIdx++;
          if (intersectedMarkers.length > 0) {
            startIdx = 0; //reset render if there's multiple marker / if there's merge of circles
          }
        }
      } catch (error) {}
    },
    getLatitudeLongitude(address) {
      if (!address) {
        return;
      }
      const geocoder = new this.google.maps.Geocoder();
      const latlong = [];

      return new Promise(function(resolve, reject) {
        geocoder.geocode({ address: address }, function(results, status) {
          if (status === "OK") {
            latlong[0] = results[0].geometry.location.lat();
            latlong[1] = results[0].geometry.location.lng();

            resolve(latlong);
          } else {
            reject(status);
          }
        });
      });
    },
    async renderCircle() {
      const sites = Object.keys(this.newCircleData);

      const listStatusValue = [];
      for (let i = 0; i < sites.length; i++) {
        const site = sites[i];
        const dataset = this.newCircleData[site];
        for (let j = 0; j < listStatusValue.length; j++) {
          const statusName = listStatusValue[j];
          const getQtyStatus = _get(dataset, statusName, 0);
          listStatusValue.push(getQtyStatus);
        }
      }
      const radiusScaler = d3
        .scaleLinear()
        .domain([this.min, this.max])
        .range([MIN_BUBBLE_RADIUS, MAX_BUBBLE_RADIUS]);
      this.radiusScaler = radiusScaler;

      for (let i = 0; i < sites.length; i++) {
        const site = sites[i];
        if (this.sitesSelected[0] !== "all" && !this.sitesSelected.includes(site)) {
          continue;
        }
        const dataset = this.newCircleData[site].siteDetails;

        const city = _get(dataset, "siteCity", "");
        const country = _get(dataset, "siteCountry", "");
        const state = _get(dataset, "siteState", "");
        const address = _get(dataset, "siteAddress");
        const latlng = _get(dataset, "latlng");

        for (const status in this.newCircleData[site].siteStatus) {
          // Iterate the circle
          const getQtyStatus = this.newCircleData[site].siteStatus[status];
          const markerInfo = {
            city,
            country,
            state,
            address,
            site,
            qtyStatus: getQtyStatus,
            siteLatLng: latlng
          };
          const circleMarker = new this.google.maps.Circle({
            idMarker: _.uniqueId(),
            strokeColor: utils.generateCircleStrokeColors(status),
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: utils.generateCircleColors(status),
            fillOpacity: 0.5,
            idStatus: status,
            map: this.map,
            qtyStatus: getQtyStatus,
            radius: this.getCircleRadius(getQtyStatus),
            center: latlng,
            markerInfos: [markerInfo]
          });
          this.decorateCircles(circleMarker);
          this.circleMarkers[status].push(circleMarker);
        }
      }
      this.renderIntersectCircle();
    },
    // Remove circle when data changed
    removeCircle(circles) {
      for (const status in circles) {
        circles[status].forEach(marker => {
          marker.setMap(null);
        });
      }

      this.circleMarkers = {}; // Remove the current circle layer
    },
    removeAllCircle() {
      for (const status in this.circleMarkers) {
        this.circleMarkers[status].forEach(marker => {
          marker.setMap(null);
        });
        this.circleMarkers[status] = [];
      } // Remove the current circle layer
    },
    decorateCircles(circle) {
      circle.addListener("mouseover", function(c) {
        const legendHtml = document.getElementById("legend-custom-google-maps");
        let content = `<div class="legend-attribute-content common-tooltip" style="color: ${getContrast(utils.generateCircleColors(circle.idStatus))};
              background-color: ${utils.generateCircleColors(circle.idStatus)};${circle.markerInfos.length > 1 && "overflow: auto;min-height:max-content; max-height:120px"}" >`;

        const { offsetX, offsetY } = c.domEvent;
        if (circle.markerInfos.length > 1) {
          // Set Style
          legendHtml.style.display = "block";
          legendHtml.style.zIndex = 5;
          legendHtml.style.backgroundColor = "#fff";
          legendHtml.style.position = "absolute";
          legendHtml.style.top = offsetY + 22 + "px";
          legendHtml.style.left = offsetX + 22 + "px";
          const markers = Array.from(circle.markerInfos).sort((a, b) => b.qtyStatus - a.qtyStatus);
          for (let index = 0; index < markers.length; index++) {
            const marker = markers[index];
            content += `
                    <div class="legend-container-row" style='display:block;margin-bottom: 0;'>
                        <div class="left-side" style='display:flex;flex-direction: row; width: 100%;'>
                            <div class='inner-left' style='font-family:Roboto-Medium;width: 30%;'>
                              <div class="legend-text margin-4">${marker.qtyStatus} <br> ${STATUS[circle.idStatus]}</div></div>
                              <div class='inner-right' style='font-family:Roboto-Regular;width: 70%;'>`;
            content += marker.site ? `<div class="legend-text">${_upperFirst(marker.site)}</div>` : "";
            content += marker.city || marker.state ? `<div class="legend-text">` : "";
            content += marker.city ? `<span>${_startCase(marker.city)}, </span>` : "";
            content += marker.state ? `<span>${_startCase(marker.state)}</span>` : "";
            content += marker.city || marker.state ? `</div>` : "";
            content += "</div></div>";
          }
        } else {
          // Set Style
          legendHtml.style.display = "block";
          legendHtml.style.zIndex = 5;
          legendHtml.style.backgroundColor = "#fff";
          legendHtml.style.position = "absolute";
          legendHtml.style.top = offsetY + 22 + "px";
          legendHtml.style.left = offsetX + 22 + "px";
          for (let index = 0; index < circle.markerInfos.length; index++) {
            const marker = circle.markerInfos[index];
            content += `
                    <div class="legend-container-row">
                        <div class="left-side">
                            <div class="legend-text margin-4">${marker.qtyStatus} ${STATUS[circle.idStatus]}</div>`;
            content += marker.site ? `<div class="legend-text">${_upperFirst(marker.site)}</div>` : "";
            content += marker.city || marker.state ? `<div class="legend-text">` : "";
            content += marker.city ? `<span>${_startCase(marker.city)},</span>` : "";
            content += marker.state ? `<span>${_startCase(marker.state)}</span>` : "";
            content += marker.city || marker.state ? `</div>` : "";
            content += "</div></div></div>";
          }
        }

        content += "</div>";
        legendHtml.innerHTML = content;
      });
      circle.addListener("mouseout", function() {
        const legendHtml = document.getElementById("legend-custom-google-maps");
        legendHtml.addEventListener("mouseenter", function() {
          legendHtml.style.display = "block";
        });

        legendHtml.addEventListener("mouseleave", function() {
          legendHtml.innerHTML = "";
          legendHtml.style.display = "none";
        });
      });
    },
    // FLy maps to center of latlong bounds
    flyOnTarget(sites) {
      const bounds = new this.google.maps.LatLngBounds();
      for (const site in sites) {
        try {
          const siteLatLng = sites[site].siteDetails.latlng;
          bounds.extend(siteLatLng);
        } catch (error) {}
      }
      this.map.fitBounds(bounds);
    },
    async prepareMapsData() {
      try {
        const sites = Object.keys(this.newCircleData);
        for (let i = 0; i < sites.length; i++) {
          const site = sites[i];
          const dataset = this.newCircleData[site].siteDetails;
          const city = _get(dataset, "siteCity", "");
          const country = _get(dataset, "siteCountry", "");
          const state = _get(dataset, "siteState", "");
          const postalCode = _get(dataset, "postalCode");
          const address = _get(dataset, "siteAddress");

          const fullAddress = `${address} ${city} ${state} ${country} ${postalCode}`.replace(/\s\s+/g, " ");
          let latlong = null;
          try {
            latlong = await this.getLatitudeLongitude(fullAddress);
          } catch (error) {
            continue;
          }
          const latlng = new this.google.maps.LatLng(latlong[0], latlong[1]);
          dataset.latlng = latlng;
        }
        if (this.newCircleData) {
          this.flyOnTarget(this.newCircleData);
        }
      } catch (error) {}
    },
    checkLegacyValue() {
      this.$api.getLegacyData(this.studyId).then(response => {
        if (response) {
          this.legacyVal = response.body;
        }
      });
      if (!this.legacyVal) {
        this.circleMarkers = {
          ACTIVE: [],
          ACTIVEWMODIFICATION: [],
          DISCONTINUED: []
        };
        STATUS = {
          ACTIVE: "Active",
          ACTIVEWMODIFICATION: "Active with Modifications",
          DISCONTINUED: "Discontinued"
        };
      }
    }
  },
  async mounted() {
    await this.prepareMapsData();
  }
};
</script>
<style lang="scss">
.legend-attribute-content {
  padding: 8px !important;

  .legend-container-row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    .left-side {
      display: flex;
      flex-direction: column;
    }

    .right-side {
      @extend .left-side;
      flex: 1;
    }

    .legend-text {
      display: inline-block;
      margin-right: 4px;
      font-size: 10px;
      font-family: "Roboto-Medium";
    }
  }

  .legend-title-custom {
    font-family: "Open Sans", sans-serif;
    font-size: 18px;
  }

  .margin-4 {
    margin-right: 4px;
  }
}
</style>
